import { DOCUMENT } from '@angular/common';
import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';

import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { WINDOW } from '@frontend/vanilla/core';
import fastdom from 'fastdom';
import { Observable, first, from, of } from 'rxjs';

import { OpenedModalsStorageService } from './opened-modals-storage.service';

interface PanOffset {
    currentY?: number;
    currentX?: number;
}

interface BackgroundAnimationOptions {
    duration: number;
    fromScale?: string;
    toScale?: string;
    fromPosition?: number;
    toPosition?: number;
    isEdpBackground?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ModalAnimationHelperService {
    private readonly _window = inject(WINDOW);
    private readonly _doc = inject(DOCUMENT);

    readonly closingRatioY = 0.2;
    readonly closingRatioX = 0.5;
    readonly minBackgroundScaleValue = 0.9;
    readonly minBackgroundScale = `scale(${this.minBackgroundScaleValue})`;
    readonly maxBackgroundScale = 'scale(1)';
    readonly minCoverElementPosition = 'translateY(-100%)';
    readonly maxCoverElementPosition = 'translateY(0%)';
    readonly animationDurationY = 500;
    readonly animationDurationX = 350;
    readonly maxBackdropOpacity = 0.25;
    private renderer: Renderer2;
    private _backgroundWindow: HTMLElement | null;

    get maxBackgroundPanValue() {
        return Math.round(this._window.screen.width * 0.33);
    }

    private get slotApp(): HTMLElement | null {
        return this._doc.querySelector<HTMLElement>('.slot-app');
    }

    private get backgroundWindow(): HTMLElement | null {
        return (this._backgroundWindow ??= this._doc.querySelector<HTMLElement>('.app-root'));
    }

    private get modalWindow(): HTMLElement | null {
        const modals = this._doc.querySelectorAll<HTMLElement>('ms-modal-window');
        return modals[modals.length - 1];
    }

    constructor(
        private openedModals: OpenedModalsStorageService,
        private rendererFactory: RendererFactory2,
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    addSlideWithScale(): void {
        this.tryHideSlotApp();

        this.animateModal(true, { currentY: 0 }, this.animationDurationY).subscribe(() => {
            this.tryShowSlotApp();
        });

        fastdom.mutate(() => {
            this._window.requestAnimationFrame(() => {
                this.animateCoverElement(this.maxCoverElementPosition, this.minCoverElementPosition, this.animationDurationY);
                this.animateBackdrop(this.maxBackdropOpacity, 0, this.animationDurationY);
                this.animateBackground({
                    fromScale: this.minBackgroundScale,
                    toScale: this.maxBackgroundScale,
                    duration: this.animationDurationY,
                    isEdpBackground: this.openedModals.edpModal && this.openedModals.count > 1,
                }).subscribe(() => {
                    if (this.openedModals.count < 1) {
                        this.renderer.removeStyle(this.backgroundWindow, 'will-change');
                        this.renderer.removeAttribute(this.backgroundWindow, 'style');
                    }
                });
            });
        });
    }

    addSlideOutHorizontal(): void {
        this.animateBackdrop(this.maxBackdropOpacity, 0, this.animationDurationX);
        this.animateBackground({ fromPosition: this.maxBackgroundPanValue, toPosition: 0, duration: this.animationDurationX }).subscribe();
        this.animateModal(true, { currentX: 0 }, this.animationDurationX).subscribe();
    }

    animateModal(shouldSlideOut: boolean, { currentY, currentX }: PanOffset, duration: number): Observable<Animation | undefined> {
        if (!this.modalWindow || duration === 0) {
            return of(undefined);
        }

        let keyframes: Keyframe[] = [];
        if (isDefined(currentY)) {
            keyframes = [{ top: `${currentY}px` }, { top: shouldSlideOut ? `${this._window.screen.height}px` : '0px' }];
        } else if (isDefined(currentX)) {
            this.renderer.setStyle(this.modalWindow, 'min-width', '100vw');
            keyframes = [{ left: `${currentX}px` }, { left: shouldSlideOut ? `${this._window.screen.width}px` : '0px' }];
        }

        return from(this.modalWindow.animate(keyframes, this.animationOptions(duration)).finished).pipe(first());
    }

    calculateAnimationTime({ currentY, currentX }: PanOffset): number {
        if (currentY) {
            const timeForDistance = Math.round(this.animationDurationY * (currentY / this._window.screen.height));
            return currentY < this._window.screen.height * this.closingRatioY ? timeForDistance : this.animationDurationY - timeForDistance;
        } else if (currentX) {
            const timeForDistance = Math.round(this.animationDurationX * (currentX / this._window.screen.width));
            return currentX < this._window.screen.width * this.closingRatioX ? timeForDistance : this.animationDurationX - timeForDistance;
        }

        return 0;
    }

    animateBackdrop(fromOpacity: number, toOpacity: number, duration: number): void {
        const backdrops = this._doc.querySelectorAll<HTMLElement>('ms-modal-backdrop');
        const modalBackdrop = backdrops[backdrops.length - 1];

        if (this.openedModals.count > 1 || !modalBackdrop || duration === 0) {
            return;
        }

        modalBackdrop.animate([{ opacity: fromOpacity }, { opacity: toOpacity }], this.animationOptions(duration));
    }

    animateCoverElement(fromPosition: string, toPosition: string, duration: number): void {
        const coverElements = this._doc.querySelectorAll<HTMLElement>('ms-modal-cover');
        const coverElement = coverElements[coverElements.length - 1];

        if (this.openedModals.count > 1 || !coverElement || duration === 0) {
            return;
        }

        coverElement.animate([{ transform: fromPosition }, { transform: toPosition }], this.animationOptions(duration)).finished.then(() => {
            if (toPosition !== this.minCoverElementPosition) {
                this.renderer.setStyle(coverElement, 'transform', this.minCoverElementPosition);
            }
        });
    }

    animateBackground({
        fromScale,
        toScale,
        duration,
        fromPosition,
        toPosition,
        isEdpBackground,
    }: BackgroundAnimationOptions): Observable<Animation | undefined> {
        if (!this.backgroundWindow || duration === 0) {
            return of(undefined);
        }

        let backgroundToAnimate = isEdpBackground ? this._doc.querySelector<HTMLElement>('.event-details-modal') : this.backgroundWindow;
        if (backgroundToAnimate) {
            let keyframes: Keyframe[] = [];
            if (isDefined(fromScale) && isDefined(toScale)) {
                this.renderer.setStyle(backgroundToAnimate, 'will-change', 'transform');
                this.setTransformOriginCoordinates();
                keyframes = [{ transform: fromScale }, { transform: toScale }];
            } else if (isDefined(fromPosition) && isDefined(toPosition)) {
                this.renderer.setStyle(backgroundToAnimate, 'display', 'block');
                this.renderer.setStyle(backgroundToAnimate, 'position', 'relative');
                this.renderer.setStyle(backgroundToAnimate, 'will-change', 'right');
                keyframes = [{ right: `${fromPosition}px` }, { right: `${toPosition}px` }];
            }

            return from(backgroundToAnimate.animate(keyframes, this.animationOptions(duration)).finished).pipe(first());
        }
        return of(undefined);
    }

    setTransformOriginCoordinates(): void {
        if (!this.backgroundWindow) {
            return;
        }

        const scrollY = this.getScrollValueFromBody();
        const viewPortY = this._window.screen.height / 2;
        const viewPortX = Math.round(this._window.screen.width / 2);
        this.renderer.setStyle(this.backgroundWindow, 'transform-origin', `${viewPortX}px ${Math.round(scrollY + viewPortY)}px`);
    }

    tryShowSlotApp(): void {
        if (this.slotApp && this.openedModals.count <= 1) {
            this.renderer.removeStyle(this.slotApp, 'visibility');
        }
    }

    tryHideSlotApp(): void {
        if (this.slotApp && this.openedModals.count <= 1) {
            this.renderer.setStyle(this.slotApp, 'visibility', 'hidden');
        }
    }

    private getScrollValueFromBody(): number {
        const scrollValue = Number.parseInt(this._doc.body.style.top.replace(/^\D+/g, ''), 10);
        if (!isNaN(scrollValue)) {
            return scrollValue;
        }
        return 0;
    }

    private animationOptions(duration: number): KeyframeAnimationOptions {
        return {
            duration,
            iterations: 1,
            easing: 'ease',
        };
    }
}
